// ID lookup — the status of one ID or up to 500 at once (national IDs or
// passport numbers): registered or not, name, area and registration date.

import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, InputBase, Typography } from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import SearchIcon from '@mui/icons-material/Search';
import { useMutation } from '@tanstack/react-query';
import { analyticsApi, AnalyticsPerson } from '@services/api/analytics';
import { areaName } from './data';
import { C, DataTable, Panel, Section } from './ui';

const MAX_IDS = 500;

const statusOf = (p: AnalyticsPerson) => (!p.found ? 'Not found' : p.status === 'REGISTERED' ? 'Registered' : 'Not registered');

export const IdLookupSection: React.FC = () => {
  const [text, setText] = useState('');
  const lookup = useMutation({ mutationFn: (ids: string) => analyticsApi.lookup(ids) });
  const count = text.split(/[\s,;]+/).filter(Boolean).length;
  const res = lookup.data;
  const go = () => { if (count > 0 && count <= MAX_IDS) lookup.mutate(text); };

  return (
    <Section id="lookup" icon={<PersonSearchIcon />} title="ID lookup" accent={C.cyan}
      subtitle="Registration status by ID or passport number · IEBC voter roll and NRB records">
      <Panel sx={{ height: 'auto', mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '0.78rem', color: C.muted, mb: 1 }}>
            Enter one ID or passport number, or paste up to {MAX_IDS} separated by commas, spaces or new lines.
          </Typography>
          <InputBase multiline minRows={3} maxRows={10} fullWidth value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) go(); }}
            placeholder="e.g. 29088631, 408941857"
            sx={{ fontSize: '0.85rem', color: C.text, backgroundColor: C.bg, border: `1px solid ${count > MAX_IDS ? C.orange : C.border}`, borderRadius: 1, p: 1.25, fontFamily: 'monospace' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.25, flexWrap: 'wrap' }}>
            <Button onClick={go} disabled={count === 0 || count > MAX_IDS || lookup.isPending} variant="contained"
              startIcon={lookup.isPending ? <CircularProgress size={16} sx={{ color: C.bg }} /> : <SearchIcon />}
              sx={{ backgroundColor: C.cyan, color: C.bg, fontWeight: 700, '&:hover': { backgroundColor: C.cyan, filter: 'brightness(1.1)' } }}>
              Look up
            </Button>
            <Button onClick={() => { setText(''); lookup.reset(); }} disabled={!text && !res} sx={{ color: C.muted }}>Clear</Button>
            <Typography sx={{ fontSize: '0.72rem', color: count > MAX_IDS ? C.orange : C.muted }}>
              {count > MAX_IDS ? `${count} IDs — at most ${MAX_IDS} at a time` : `${count} ID${count === 1 ? '' : 's'}`}
            </Typography>
          </Box>
        </Box>
      </Panel>

      {lookup.error && <Typography sx={{ color: C.orange, fontSize: '0.8rem', mb: 2 }}>{(lookup.error as any)?.message || 'Lookup failed.'}</Typography>}

      {res && (
        <Panel sx={{ height: 'auto' }} title={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            Results
            <Chip size="small" label={`${res.counts.registered} registered`} sx={{ height: 20, color: C.green, border: `1px solid ${C.green}66`, backgroundColor: 'transparent' }} />
            <Chip size="small" label={`${res.counts.unregistered} not registered`} sx={{ height: 20, color: C.orange, border: `1px solid ${C.orange}66`, backgroundColor: 'transparent' }} />
            <Chip size="small" label={`${res.counts.not_found} not found`} sx={{ height: 20, color: C.muted, border: `1px solid ${C.border}`, backgroundColor: 'transparent' }} />
          </Box>
        }>
          <DataTable<AnalyticsPerson> rows={res.results} rowKey={(p) => p.national_id} maxHeight={560} fileName="id-lookup" searchPlaceholder="Search ID…"
            columns={[
              { key: 'id', label: 'ID number', value: (p) => p.national_id },
              { key: 'status', label: 'Status', value: (p) => statusOf(p) },
              { key: 'name', label: 'Name', value: (p) => p.full_name || '' },
              { key: 'tribe', label: 'Tribe', value: (p) => p.tribe || '' },
              { key: 'county', label: 'County', value: (p) => areaName(p.county || '') },
              { key: 'sub', label: 'Subcounty / country', value: (p) => areaName(p.subcounty || '') },
              { key: 'div', label: 'Division / mission', value: (p) => areaName(p.division || '') },
              { key: 'loc', label: 'Location', value: (p) => areaName(p.location || '') },
              { key: 'constituency', label: 'Constituency', value: (p) => p.constituency || '' },
              { key: 'ward', label: 'Ward', value: (p) => p.ward || '' },
              { key: 'pc', label: 'Polling station', value: (p) => p.polling_centre || '' },
              { key: 'date', label: 'Registered on', value: (p) => (p.registration_date ? p.registration_date + (p.registered_via_app ? ' (mobile app)' : '') : '') },
            ]} />
        </Panel>
      )}
    </Section>
  );
};
